import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import type { Product } from "@/lib/types";
import { useProductContext } from "@/context/ProductContext";

const PAGE_SIZE = 20;

const allCategories = [
  "Todos",
  "Pasteles Grandes",
  "Pasteles Medianos",
  "Pasteles Individuales",
  "Galletas",
  "Gelatinas",
  "Panqués y Roscas",
  "Tartas",
  "Sin Azúcar",
  "Productos para perrito",
  "Helados",
];

const badgeColors: Record<string, string> = {
  FAVORITO: "bg-[#f9b8c8] text-[#2d1a0e]",
  NUEVO: "bg-[#00b4c8] text-white",
  TEMPORADA: "bg-[#fde68a] text-[#2d1a0e]",
  PERSONALIZABLE: "bg-[#c9b8f0] text-[#2d1a0e]",
  CLÁSICO: "bg-[#c9b8f0] text-[#2d1a0e]",
};


function ProductModal({ product, onClose }: { product: Product; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl overflow-hidden max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative overflow-hidden rounded-t-3xl">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="w-full h-[400px] object-cover object-center"
          />

          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 text-[#2d1a0e] hover:bg-white cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>

          {product.badge && (
            <span
              className={`absolute top-4 left-4 text-xs font-bold px-3 py-1 rounded-full ${
                badgeColors[product.badge] || "bg-gray-500 text-white"
              }`}
            >
              {product.badge}
            </span>
          )}
        </div>

        <div className="p-6 md:p-8">
          <p className="text-xs text-[#d4607a] font-semibold uppercase tracking-wider mb-1">
            {product.category}
          </p>

          <h2
            className="text-2xl font-bold text-[#2d1a0e] mb-3"
          >
            {product.name}
          </h2>

          <p className="text-[#6b5b6e] text-sm leading-relaxed mb-6">
            {product.description}
          </p>

          <div className="mb-6">
            <p className="text-xs text-[#6b5b6e] mb-1">Precio</p>
            <p className="text-3xl font-bold text-[#2d1a0e]">
              <span className="text-base font-normal text-[#7a5c44]">$</span>
              {product.price}
              <span className="text-sm font-normal text-[#7a5c44] ml-1">MXN</span>
            </p>
          </div>

          <a
            href={`https://wa.me/5217291317070?text=Hola! Me interesa el producto: ${encodeURIComponent(
              product.name
            )} ($${product.price} MXN)`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 bg-[#00b4c8] text-white font-semibold py-3.5 rounded-full hover:bg-[#0099aa] transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-whatsapp-line"></i> Pedir por WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}

function ProductCardSkeleton() {
  return (
    <div className="bg-[#fdf5ff] rounded-2xl overflow-hidden animate-pulse">
      <div className="aspect-square bg-[#e8e0f8]"></div>
      <div className="p-4 space-y-2">
        <div className="h-3 bg-[#e8e0f8] rounded-full w-1/3"></div>
        <div className="h-4 bg-[#e8e0f8] rounded-full w-3/4"></div>
        <div className="h-3 bg-[#e8e0f8] rounded-full w-full"></div>
        <div className="h-3 bg-[#e8e0f8] rounded-full w-2/3"></div>
        <div className="flex justify-between items-center pt-1">
          <div className="h-6 bg-[#e8e0f8] rounded-full w-16"></div>
          <div className="h-4 bg-[#e8e0f8] rounded-full w-12"></div>
        </div>
      </div>
    </div>
  );
}

export default function MenuPage() {
  const { products: allProducts, loading, error, refreshProducts } = useProductContext();
  const [searchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState("Todos");
  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [sortBy, setSortBy] = useState("priority");
  const [currentPage, setCurrentPage] = useState(0);

  // Sync category from URL
  useEffect(() => {
    const cat = searchParams.get("categoria");
    if (cat && allCategories.includes(cat)) {
      setActiveCategory(cat);
    }
  }, [searchParams]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [activeCategory, search, sortBy]);

  // Open product modal from URL param
  useEffect(() => {
    const productoId = searchParams.get("producto");
    if (productoId && allProducts.length > 0) {
      const found = allProducts.find((p) => p.id === Number(productoId));
      if (found) setSelectedProduct(found);
    }
  }, [searchParams, allProducts]);

  // Client-side filtering + sorting
  const filteredProducts = useMemo(() => {
    let result = allProducts.filter((p) => p.active);

    if (activeCategory !== "Todos") {
      result = result.filter((p) => p.category === activeCategory);
    }

    if (search.trim() !== "") {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q)
      );
    }

    if (sortBy === "price-asc") {
      result = [...result].sort((a, b) => a.price - b.price);
    } else if (sortBy === "price-desc") {
      result = [...result].sort((a, b) => b.price - a.price);
    } else {
      result = [...result].sort((a, b) => a.priority - b.priority);
    }

    return result;
  }, [allProducts, activeCategory, search, sortBy]);

  // Paginate
  const totalPages = Math.ceil(filteredProducts.length / PAGE_SIZE);
  const pageProducts = filteredProducts.slice(
    currentPage * PAGE_SIZE,
    (currentPage + 1) * PAGE_SIZE
  );

  const showingFrom = filteredProducts.length > 0 ? currentPage * PAGE_SIZE + 1 : 0;
  const showingTo = Math.min((currentPage + 1) * PAGE_SIZE, filteredProducts.length);

  const handlePageChange = (pageIndex: number) => {
    if (pageIndex < 0 || pageIndex >= totalPages) return;
    setCurrentPage(pageIndex);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRetry = () => {
    refreshProducts();
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative h-64 md:h-80 flex items-end pb-10">
        <img
          src="https://storage.readdy-site.link/project_files/e7ad97af-bf5b-41b1-80d2-ae3fa35ba9b1/9c613d09-4b42-45f2-8ebc-dde79d31ec69_668565906_971106595446815_4781983870149947149_n.jpg?v=bed18e057f728f560d3042bc7c7ccbef"
          alt="Menú"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full pt-20">
          <p className="text-[#7ee8f4] text-xs font-semibold uppercase tracking-widest mb-2">
            Inicio / Menú
          </p>
          <h1
            className="text-4xl md:text-5xl font-bold text-white"
          >
            Nuestro Menú
          </h1>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-20 z-30 bg-white border-b border-[#f5e6d3] py-4 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {allCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`text-xs font-semibold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-[#00b4c8] text-white"
                    : "bg-[#e0f7fa]/60 text-[#6b5b6e] hover:bg-[#e0f7fa]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-52">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#9b8aae] text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar producto..."
                className="w-full pl-9 pr-4 py-2 rounded-full border border-[#b2ebf2] text-sm text-[#2d1a0e] placeholder-[#9b8aae] focus:outline-none focus:border-[#00b4c8] bg-[#e0f7fa]/30"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-xs border border-[#b2ebf2] rounded-full px-3 py-2 text-[#6b5b6e] bg-[#e0f7fa]/30 focus:outline-none cursor-pointer"
            >
              <option value="priority">Más Popular</option>
              <option value="price-asc">Precio: Menor</option>
              <option value="price-desc">Precio: Mayor</option>
            </select>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {!loading && !error && filteredProducts.length > 0 && (
            <p className="text-sm text-[#6b5b6e] mb-6">
              Mostrando {showingFrom}–{showingTo} de {filteredProducts.length} producto{filteredProducts.length !== 1 ? "s" : ""}
            </p>
          )}

          {error ? (
            <div className="text-center py-20">
              <i className="ri-error-warning-line text-5xl text-[#f4a261]/60"></i>
              <p className="text-[#6b5b6e] mt-4 mb-6">{error}</p>
              <button
                onClick={handleRetry}
                className="inline-flex items-center gap-2 bg-[#00b4c8] text-white font-semibold px-6 py-3 rounded-full hover:bg-[#0099aa] transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-refresh-line"></i> Reintentar
              </button>
            </div>
          ) : loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <ProductCardSkeleton key={i} />
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-search-line text-5xl text-[#00b4c8]/40"></i>
              <p className="text-[#6b5b6e] mt-4">No encontramos productos con esa búsqueda.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {pageProducts.map((product) => (
                  <div
                    key={product.id}
                    className="group bg-[#e0f7fa]/20 rounded-2xl overflow-hidden hover:shadow-sm transition-all duration-300 cursor-pointer"
                    onClick={() => setSelectedProduct(product)}
                  >
                    <div className="relative overflow-hidden aspect-square">
                      <img
                        src={product.image}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      {product.badge && (
                        <span
                          className={`absolute top-3 left-3 text-xs font-bold px-2.5 py-1 rounded-full ${
                            badgeColors[product.badge] || "bg-gray-500 text-white"
                          }`}
                        >
                          {product.badge}
                        </span>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                        <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-[#2d1a0e] text-xs font-semibold px-4 py-2 rounded-full">
                          Ver detalles
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-[#00b4c8] font-medium mb-1">{product.category}</p>
                      <h3 className="font-bold text-[#2d1a0e] text-sm mb-1 leading-tight">
                        {product.name}
                      </h3>
                      <p className="text-xs text-[#6b5b6e] mb-3 line-clamp-2 leading-relaxed">
                        {product.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xl font-bold text-[#2d1a0e]">
                          <span className="text-xs font-normal text-[#7a5c44]">$</span>
                          {product.price}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProduct(product);
                          }}
                          className="text-xs font-semibold text-[#00b4c8] flex items-center gap-1 hover:gap-2 transition-all cursor-pointer whitespace-nowrap"
                        >
                          Pedir <i className="ri-arrow-right-line"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-10">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 0}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer disabled:opacity-25 disabled:cursor-default text-[#2d1a0e] bg-[#e0f7fa]/60 hover:bg-[#e0f7fa] whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i> Anterior
                  </button>

                  <span className="text-xs text-[#9b8aae] font-medium">
                    Página {currentPage + 1} de {totalPages}
                  </span>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer disabled:opacity-25 disabled:cursor-default text-[#2d1a0e] bg-[#e0f7fa]/60 hover:bg-[#e0f7fa] whitespace-nowrap"
                  >
                    Siguiente <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {selectedProduct && (
        <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
      )}

      <Footer />
    </div>
  );
}